/**
 * Map a typed ``TourStep`` to a react-joyride v3 ``Step`` — the single place
 * the page=spotlight / action=beacon convention (issue #1341) AND the
 * "tooltip is ALWAYS fully on-screen" guarantee (issue #1357) are applied.
 *
 * Why this exists:
 *  - react-joyride v3 reads ``skipBeacon`` (NOT the v2 ``disableBeacon``, which
 *    it silently ignores). The engine previously passed ``disableBeacon: true``,
 *    so v3 fell back to its default of showing a beacon dot first — a floating
 *    dot near the top that highlighted nothing on a whole-page step. This helper
 *    sets ``skipBeacon`` correctly.
 *  - ``kind`` (default ``"page"``) is the convention: a page / section / center
 *    step spotlights its region and goes straight to the tooltip; an ``"action"``
 *    step keeps the beacon so the user clicks the highlighted button to reveal
 *    the tooltip (the right affordance for a "press this button / open this
 *    modal" step — what the follow-up action/modal tour steps build on).
 *  - VIEWPORT CLAMP (issue #1357, corrected by #1367): react-joyride v3 positions
 *    tooltips with Floating UI (``@floating-ui/react-dom``). The live #1357 bug:
 *    when the anchored target is TALLER than the viewport (the whole "Add a
 *    Mandate" form container), no amount of ``shift`` can keep a tooltip anchored
 *    to it on-screen — there is no edge of the target that both sits inside the
 *    viewport AND leaves room for the tooltip. We make the guarantee ENGINE-LEVEL,
 *    not a per-step patch: EVERY mapped step carries ``floatingOptions`` that
 *    append {@link viewportClampMiddleware}, which detects a taller-/wider-than-
 *    viewport reference and centres the tooltip in the viewport (the same fallback
 *    react-joyride uses for ``placement: "center"``). ``deepMerge`` in
 *    react-joyride means a per-step ``floatingOptions`` override still composes
 *    with the props-level default.
 *  - The centring middleware is the WHOLE fix and is a strict NO-OP for a normal,
 *    well-placed anchor (the overflow check short-circuits), so a small target
 *    keeps its computed ``placement``. #1357 ALSO set
 *    ``shiftOptions: { crossAxis: true }`` (shift on both axes), but that REGRESSED
 *    the small left-anchored sidebar steps (#1367): for a ``placement:"right"``
 *    step on a sidebar nav item inside a ``position:fixed`` aside (so the floater
 *    uses ``strategy:"fixed"``), the cross-axis shift dragged the floater's X to
 *    the viewport's left padding, jamming the tooltip over the sidebar with its
 *    caret pointing off-screen — the "right" placement no longer honoured. The
 *    cross-axis shift was never needed (the clamp already handles the only case it
 *    targeted, the oversized form), so #1367 drops it: ``shift`` reverts to
 *    react-joyride's default MAIN-axis-only behaviour and the clamp alone keeps the
 *    oversized-target guarantee.
 *
 * Pure (no React / branding) so it is unit-testable; copy interpolation is the
 * caller's job (it needs the live branded agency name).
 */
import type { FloatingOptions, Step } from "react-joyride";
import type { Middleware, MiddlewareState } from "@floating-ui/react-dom";
import type { TourStep } from "./types";
import { anchorSelector } from "./anchorSelector";

/**
 * Fallback scroll offset (px) for the below-the-fold scroll math (issue #1981),
 * used when the live sticky header cannot be measured (SSR / test, or the header
 * is not yet mounted). 96px comfortably clears the tallest header state plus
 * breathing room so the spotlight never lands behind the sticky top bar.
 */
export const TOUR_SCROLL_OFFSET_FALLBACK = 96;

/**
 * Derive react-joyride's ``scrollOffset`` from the LIVE sticky header (issue
 * #1981). Both shells render their ``sticky top-0 z-30`` header as the FIRST
 * CHILD of the scrolling ``<main>`` (per #1739), so a target scrolled to the top
 * of that scroll parent lands UNDER the header and the spotlight is occluded.
 * react-joyride v3's default ``scrollOffset`` (20px) is far too small to clear
 * the ~52-64px header; a LARGER offset scrolls the target further down, clearing
 * it.
 *
 * We read the offset from the ``[data-tour-sticky-header]`` element (tagged in
 * CrmLayout/AdminLayout) at call time so it reflects the real header height in
 * BOTH shells and across breakpoints, rather than a hardcoded guess: header
 * height + 16px breathing room (so the spotlight cutout AND its padding fully
 * clear the header), floored at a sane minimum. Guarded for non-DOM
 * environments.
 */
export function stickyHeaderOffset(): number {
  if (typeof document === "undefined") return TOUR_SCROLL_OFFSET_FALLBACK;
  const header = document.querySelector("[data-tour-sticky-header]");
  if (!header) return TOUR_SCROLL_OFFSET_FALLBACK;
  const height = header.getBoundingClientRect().height;
  // +16px so the cutout's spotlightPadding clears the header too; never less
  // than a sane floor (a 0-height/unmeasured header must still clear the bar).
  return Math.max(Math.round(height) + 16, 48);
}

/**
 * Bring a hint's control into view before the tour engine measures it (#3633).
 * react-joyride never scrolls a one-step hint (it is the first step), so this is
 * the only scroll a hint gets. ``inline: "nearest"`` slides a control into view
 * inside a sideways-scrolling strip (a record page's tabs on a phone) and leaves
 * the page alone vertically when the control is already clearly visible. A
 * control that is above the fold, below it, under the sticky shell header
 * (#1981) or covered by a page's own sticky bar (a record's title bar) is
 * centred instead: a plain ``block: "nearest"`` would pin it to the very top of
 * the scrolling ``<main>``, under those bars, hiding the spotlight.
 */
export function scrollHintTargetIntoView(
  el: HTMLElement,
  headerOffset: number = stickyHeaderOffset(),
): void {
  if (typeof el.scrollIntoView !== "function") return;
  const hidden = hintTargetObscured(el, headerOffset);
  el.scrollIntoView({ block: hidden ? "center" : "nearest", inline: "nearest" });
}

/** True when the control is off screen vertically, under the header, or covered. */
export function hintTargetObscured(el: HTMLElement, headerOffset: number): boolean {
  const rect = el.getBoundingClientRect();
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight || 0;
  if (rect.top < headerOffset || (viewportHeight > 0 && rect.bottom > viewportHeight)) {
    return true;
  }
  // Something else painted over the control's centre (a sticky bar) hides it.
  // Only judged when the centre is inside the viewport horizontally: a tab that
  // is off to the side in a sideways strip is brought in by the inline scroll.
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  if (typeof document.elementFromPoint !== "function" || x < 0 || x > viewportWidth) {
    return false;
  }
  const top = document.elementFromPoint(x, y);
  return top !== null && top !== el && !el.contains(top) && !top.contains(el);
}

/** A page/section step is the DEFAULT; only ``kind === "action"`` is a beacon. */
export function isActionStep(step: Pick<TourStep, "kind" | "target">): boolean {
  // A "center" narrative step is always a page step — it has no anchor to put a
  // beacon on, so an action kind there would be meaningless.
  return step.target !== "center" && step.kind === "action";
}

/**
 * Spotlight cutout padding (px) so a page/section region reads as clearly
 * highlighted. A thin header row would otherwise spotlight as a sliver; an
 * action step hugs its button tightly; a center step has no anchor.
 */
export function spotlightPaddingFor(
  step: Pick<TourStep, "kind" | "target">,
): number {
  if (step.target === "center") return 0;
  return isActionStep(step) ? 6 : 12;
}

/**
 * Minimum margin (px) kept between a clamped tooltip and the viewport edge —
 * matches react-joyride's own default ``shift`` padding so the two behaviours
 * read consistently.
 */
export const VIEWPORT_CLAMP_PADDING = 10;

/**
 * Resolve the viewport size. Guarded for non-DOM (test/SSR) environments where
 * ``window`` may be undefined or report 0 — a 0 viewport must NOT be treated as
 * "everything is taller than the viewport" (that would force-centre every
 * tooltip), so a non-positive dimension disables the clamp for that axis.
 */
function viewportSize(): { width: number; height: number } {
  if (typeof window === "undefined") return { width: 0, height: 0 };
  return {
    width: window.innerWidth || 0,
    height: window.innerHeight || 0,
  };
}

/**
 * Decide whether a reference rect overflows the viewport on either axis such
 * that an edge-anchored tooltip cannot be guaranteed on-screen. Exported pure so
 * the regression test can assert the geometry decision without a DOM.
 *
 * A target is "oversized" when it is taller (or wider) than the viewport minus
 * twice the clamp padding — there is then no edge of it that both sits inside
 * the viewport AND leaves room for the tooltip, so anchoring to it risks an
 * offscreen tooltip and we centre instead.
 */
export function referenceOverflowsViewport(
  reference: { width: number; height: number },
  viewport: { width: number; height: number },
  padding = VIEWPORT_CLAMP_PADDING,
): boolean {
  const tallerThanViewport =
    viewport.height > 0 &&
    reference.height > viewport.height - padding * 2;
  const widerThanViewport =
    viewport.width > 0 && reference.width > viewport.width - padding * 2;
  return tallerThanViewport || widerThanViewport;
}

/**
 * Floating UI middleware (issue #1357): when the anchored reference (the
 * spotlighted region) is larger than the viewport, override the computed
 * coordinates to CENTRE the floating tooltip in the viewport, mirroring the
 * ``placement: "center"`` fallback react-joyride uses for anchorless steps. For
 * a normal-sized target it is a no-op and the standard offset/flip/shift chain
 * positions the tooltip against the target as usual.
 *
 * This runs LAST in the chain (appended after ``shift``) so it has the final say
 * on a pathological geometry, and it reads ``rects.floating`` so the centred box
 * is fully inside the viewport regardless of tooltip size.
 */
export const viewportClampMiddleware: Middleware = {
  name: "tourViewportClamp",
  fn(state: MiddlewareState) {
    const { rects } = state;
    const viewport = viewportSize();
    if (!referenceOverflowsViewport(rects.reference, viewport)) {
      return {};
    }
    // Centre the tooltip in the viewport, but never push it past the top/left
    // edge if it is itself taller/wider than the viewport (keep the controls
    // reachable at the top-left rather than clipping them above the fold).
    const x = Math.max(
      VIEWPORT_CLAMP_PADDING,
      (viewport.width - rects.floating.width) / 2,
    );
    const y = Math.max(
      VIEWPORT_CLAMP_PADDING,
      (viewport.height - rects.floating.height) / 2,
    );
    return { x, y };
  },
};

/**
 * The engine-level floating config every tour step inherits (issue #1357, corrected
 * by #1367): append the oversized-target centring fallback ({@link
 * viewportClampMiddleware}). Exported so the provider can ALSO pass it as the
 * props-level default on ``<Joyride>`` (belt-and-braces — both compose via
 * react-joyride's deepMerge).
 *
 * We deliberately DO NOT override ``shiftOptions``: react-joyride's default
 * ``shift({ padding: 10 })`` clamps along the MAIN axis only, which is exactly
 * right — it stops a tooltip spilling off the side it is placed on, while leaving
 * a normal ``placement:"right"`` (etc.) anchor at its computed position. #1357
 * additionally set ``crossAxis: true`` here, but that REGRESSED small left-anchored
 * sidebar steps (#1367 — the floater jammed to the viewport's left edge over the
 * sidebar, caret off-screen). The clamp middleware below is the whole guarantee for
 * the oversized-target case and is a strict no-op otherwise, so the cross-axis shift
 * was both unnecessary and harmful; it is gone.
 */
export const TOUR_FLOATING_OPTIONS: Partial<FloatingOptions> = {
  middleware: [viewportClampMiddleware],
};

/**
 * Build the react-joyride step. ``interpolate`` injects the branded agency name
 * into the (already token-bearing) title/body copy.
 */
export function toJoyrideStep(
  step: TourStep,
  interpolate: (copy: string) => string,
): Step {
  const isCenter = step.target === "center";
  return {
    target: isCenter ? "body" : anchorSelector(step.target),
    title: interpolate(step.title),
    content: interpolate(step.body),
    placement: step.placement ?? (isCenter ? "center" : "auto"),
    // page=spotlight (skipBeacon true) / action=beacon (skipBeacon false).
    skipBeacon: !isActionStep(step),
    spotlightPadding: spotlightPaddingFor(step),
    // Viewport-clamp guarantee — inherited by EVERY step (issue #1357). A center
    // step is already viewport-centred by react-joyride, so the clamp is a no-op
    // there; we still attach it for uniformity (the overflow check short-circuits).
    floatingOptions: TOUR_FLOATING_OPTIONS,
    // Hand-off step (D9 WP24, #3551): the user must be free to click a record
    // in the list, so no overlay (nothing to click "through"), no overlay-click
    // cancel and no focus trap holding the keyboard in the tooltip.
    ...(isHandoffStep(step)
      ? { hideOverlay: true, overlayClickAction: false as const, disableFocusTrap: true }
      : {}),
  };
}

/** A hand-off step waits for the user to open a record (``advanceOnRoute``). */
export function isHandoffStep(step: Pick<TourStep, "advanceOnRoute">): boolean {
  return Boolean(step.advanceOnRoute);
}
